<template>
    <div class="v-app" v-bind:class="{ 'v-loading': isPageLoading, 'v-loading-first': isFirstLoading, '-dark': isDarkTemplate, 'navigation-opened': isNavMenuOpened, '-global-overlay': isGlobalOverlayEnabled }">

        <preloader-component :is-page-loading="isPageLoading" v-bind:aria-hidden="[ isPageLoading ? 'false' : 'true'  ]"></preloader-component>
        <towards-component></towards-component>

        <youtube-video-component
            v-if="isFullscreenVideoOpened"
            v-bind:aria-hidden="[ isFullscreenVideoOpened ? 'false' : 'true'  ]"></youtube-video-component>
        <hotspot-panel-component
            v-ref:hotspot-panel
            v-if="isHotspotPanelOpened"
            v-bind:aria-hidden="[ isHotspotPanelOpened ? 'false' : 'true'  ]"></hotspot-panel-component>

        <navigation-component></navigation-component>

		<router-view
            v-el:router-view v-view-directive
            v-bind:route-name="$route.name"
            class="v-router-view"
            keep-alive
            :is-dark-template.sync="isDarkTemplate"
            v-bind:aria-hidden="[ isHotspotPanelOpened ? 'true' : 'false'  ]"></router-view>

        <footer-component v-el:footer></footer-component>

    </div>
</template>

<script>
    import store from '../vuex/store'
    import Navigation from '../components/Navigation'
    import Footer from '../components/Footer'
    import Towards from '../components/Towards'
    import Preloader from '../components/Preloader'
    import Youtube from '../components/YoutubeVideo'
    import HotspotPanel from '../components/HotspotPanel'
    import { closeHotspotPanel, closeFullscreenVideo, setMobileAgent, setMobileSize, setBrowserIE, setBrowserSafari } from '../vuex/actions'
    import _throttle from 'lodash.throttle'

    export default {
        store,

        vuex: {
            getters: {
                isGlobalOverlayEnabled: state => state.isGlobalOverlayEnabled,
                isFullscreenVideoOpened: state => state.isFullscreenVideoOpened,
                isHotspotPanelOpened: state => state.isHotspotPanelOpened,
                isMobileAgent: state => state.isMobileAgent,
                isMobileSize: state => state.isMobileSize,
                isBrowserIE: state => state.isBrowserIE,
                isBrowserSafari: state => state.isBrowserSafari,
                isNavMenuOpened: state => state.isNavMenuOpened,
                isSoundSupported: state => state.isSoundSupported
            },
            actions: {
                closeHotspotPanel,
                closeFullscreenVideo,
                setMobileAgent,
                setMobileSize,
                setBrowserIE,
                setBrowserSafari
            }
        },

        data: function () {
            return {
                template: require('../../static/data/_.json'),
                isPageLoading: true,
                isDarkTemplate: false,
                isFirstLoading: true,
                startDelay: false,
                mobileBreakpointX: 1024,
                mobileBreakpointY: 640
            }
        },

        events: {
            'viewport-bind': function (routeName) {
                this.$nextTick(function () {
                    this.$broadcast('viewport-bind', routeName)
                })
            },
            'viewport-unbind': function (routeName) {
                this.$broadcast('viewport-unbind', routeName)
            },
            'loading-start': function () {
                this.$set('isPageLoading', true)

                if (this.isHotspotPanelOpened) this.closeHotspotPanel()
                if (this.isFullscreenVideoOpened) this.closeFullscreenVideo()
            },
            'loading-end': function () {
                window.requestAnimationFrame(this.delayPreloader)

                if (window.phantom) {
                    setTimeout(function () {
                        let event = document.createEvent('CustomEvent')
                        event.initCustomEvent('prerender-ready', true, true, {})
                        window.dispatchEvent(event)
                    }, 3000)
                }
            },
            'play-sound-once': function (identifier) {
                this.$broadcast('play-sound-once', identifier)
            }
        },

        created: function () {

            // detect mobile user agent
            if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0, 4))) {
                this.setMobileAgent(true)
            } else {
                this.setMobileAgent(false)
            }

            // detect mobile responsive size
            this.calcIfMobileSize()
            window.addEventListener('resize', _throttle(function () {
                this.calcIfMobileSize()
            }.bind(this), 500))

            // detect IE browser
            let ua = window.navigator.userAgent
            let msie = ua.indexOf('MSIE ')
            let isIE = (msie > 0 || !!ua.match(/Trident.*rv:11\./))
            this.setBrowserIE(isIE)

            // detect Safari browser
            let isSafari = /^((?!chrome|android).)*safari/i.test(ua)
            this.setBrowserSafari(isSafari)
        },

        methods: {
            'delayPreloader': function (timestamp) {
                if (!this.startDelay) this.startDelay = timestamp

                let progress = timestamp - this.startDelay

                if (progress < 500) {
                    window.requestAnimationFrame(this.delayPreloader)
                } else {
                    this.$set('isPageLoading', false)
                    this.$set('isFirstLoading', false)
                }
            },
            'calcIfMobileSize': function () {
                if (window.innerWidth < this.mobileBreakpointX || window.innerHeight < this.mobileBreakpointY) {
                    this.setMobileSize(true)
                } else {
                    this.setMobileSize(false)
                }
            }
        },

        components: {
            'navigation-component': Navigation,
            'footer-component': Footer,
            'towards-component': Towards,
            'preloader-component': Preloader,
            'youtube-video-component': Youtube,
            'hotspot-panel-component': HotspotPanel
        }
    }
</script>

<style lang="scss">
    @import '../assets/sass/global';
    @import '../assets/sass/main';

    $viewport-vpaddings: 10rem;

    .v-app {
        width: 100%;
        height: 100%;
        overflow: hidden;
        background-color: $body-bgColor;
        color: $typo-color;
        background-image: url('../assets/images/background.jpg');
        background-size: cover;
        background-position: center center;
        background-repeat: no-repeat;

        // background: radial-gradient(#555558, #1d1e1f);
        // filter: progid:DXImageTransform.Microsoft.gradient(GradientType=2,startColorstr='#555558', endColorstr='#1d1e1f');

        &.-dark {
            background: $dark-black;
            color: $white;
        }
    }

    .-global-overlay .v-router-view {
        z-index: 3;
    }

    .v-router-view {
        transition: opacity .8s ease, transform .8s ease;
        opacity: 1;
        transform: translateY(0);

        width: 100%;
        height: 100%;
        padding: $viewport-vpaddings*0.8 $global-viewport-padding-mobile $global-viewport-padding-mobile;
        position: relative;

        @include gridle_state(large) {
            padding: $viewport-vpaddings $global-viewport-padding $viewport-vpaddings*0.75;
        }

        .content-wrapper {
            position: relative;
            width: 100%;
            height: 100%;
        }
    }

    .v-loading-first .v-router-view {
        opacity: 0;
    }

    .v-loading .v-router-view {
        transform: translateY(10rem);
    }
</style>



// WEBPACK FOOTER //
// App.vue?001fd024